import { forwardRef, useId, useEffect, useRef, type InputHTMLAttributes } from 'react'
import { cn } from '../../utils.js'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Visible label text */
  label: string
  /** Indeterminate state (partially checked) */
  indeterminate?: boolean
}

/**
 * Accessible checkbox with label and indeterminate state support.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, indeterminate = false, className, id: externalId, ...props },
  ref,
) {
  const generatedId = useId()
  const id = externalId ?? generatedId
  const internalRef = useRef<HTMLInputElement | null>(null)

  const setRef = (el: HTMLInputElement | null) => {
    internalRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el
  }

  useEffect(() => {
    if (internalRef.current) {
      internalRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  return (
    <div className="flex items-center gap-2">
      <input
        ref={setRef}
        id={id}
        type="checkbox"
        className={cn(
          'h-4 w-4 rounded-[var(--radius-sm)] border border-[var(--color-border)]',
          'text-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-0',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'accent-[var(--color-primary)]',
          className,
        )}
        {...props}
      />
      <label
        htmlFor={id}
        className="text-sm text-[var(--color-text)] select-none cursor-pointer"
      >
        {label}
      </label>
    </div>
  )
})
